//import * as XLSX from "xlsx";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Grid,
  Box,
  Button,
  Stack,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Divider,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  TextField,
} from "@mui/material";
import { useTableFilter } from "@/shared";
import DateRangePicker from "@/shared/components/ui/CustomSearch/DateRangePicker";
import { format } from "date-fns";
import Flatpickr from "react-flatpickr";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { Description, PictureAsPdf, LocationCity, Category, ViewList, BarChart, Visibility, Search, Refresh } from "@mui/icons-material";
import { useObtenerReporteGeneralVentas, useObtenerFiltrosVentas } from "@/shared/actions/erp";
import jsPDF from "jspdf";

// Función helper para cargar imagen
const loadImage = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas context error"));
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => reject(new Error("Image load error"));
    img.src = url;
  });
};

export default function ResumenReporteVenta() {
  const {
    dateBefore,
    dateAfter,
    onChangeRangeDate,
    cleanFilter,
  } = useTableFilter();

  const [ciudadFilter, setCiudadFilter] = useState<string[]>([]);
  const [canalFilter, setCanalFilter] = useState<string[]>([]);
  const [planFilter, setPlanFilter] = useState<string[]>([]);
  const [tipoFilter, setTipoFilter] = useState<string[]>([]);
  const [allData, setAllData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [dateRangeError, setDateRangeError] = useState<string>("");

  // Estados para búsqueda en selectores
  const [searchCiudad, setSearchCiudad] = useState<string>("");
  const [searchCanal, setSearchCanal] = useState<string>("");
  const [searchPlan, setSearchPlan] = useState<string>("");
  const [searchTipo, setSearchTipo] = useState<string>("");

  // Estados para exportación
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportType, setExportType] = useState<"excel" | "pdf" | "">("");
  const [exportProgress, setExportProgress] = useState<number>(0);

  // Estado para toggle de vista
  const [vistaTabla, setVistaTabla] = useState<boolean>(false);

  // Estados para modales de detalles
  const [openModalPlanes, setOpenModalPlanes] = useState<boolean>(false);
  const [openModalVentas, setOpenModalVentas] = useState<boolean>(false);

  const flatpickrRef = useRef<Flatpickr>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const obtenerReporteMutation = useObtenerReporteGeneralVentas();
  const { data: filtrosData, isLoading: isLoadingFiltros, refetch: refetchFiltros } = useObtenerFiltrosVentas();

  // Fechas del mes anterior
  const { firstDayPrevMonth, lastDayPrevMonth } = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    return {
      firstDayPrevMonth: format(firstDay, "yyyy-MM-dd"),
      lastDayPrevMonth: format(lastDay, "yyyy-MM-dd")
    };
  }, []);

  // Cargar datos
  const loadData = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    setIsLoading(true);
    setError("");

    try {
      const filters: any = {
        fecha_desde: dateAfter || firstDayPrevMonth,
        fecha_hasta: dateBefore || lastDayPrevMonth,
      };

      const response = await obtenerReporteMutation.mutateAsync(filters);

      if (abortControllerRef.current?.signal.aborted) return;

      if (response.data && Array.isArray(response.data)) {
        setAllData(response.data);
      } else {
        setError("Estructura de respuesta inesperada");
      }
    } catch (err: any) {
      if (err?.name === 'AbortError' || abortControllerRef.current?.signal.aborted) return;
      setError(err?.message || "Error al cargar datos");
    } finally {
      setIsLoading(false);
    }
  }, [dateAfter, dateBefore, firstDayPrevMonth, lastDayPrevMonth]);

  // Establecer fechas por defecto
  useEffect(() => {
    if (!dateAfter && !dateBefore) {
      const startDate = new Date(firstDayPrevMonth);
      const endDate = new Date(lastDayPrevMonth);
      onChangeRangeDate([startDate, endDate]);
    }
  }, []);

  // Validar rango de fechas
  useEffect(() => {
    if (!dateAfter || !dateBefore) {
      setDateRangeError("");
      return;
    }

    const fechaDesde = new Date(dateAfter);
    const fechaHasta = new Date(dateBefore);
    const diffDays = Math.ceil((fechaHasta.getTime() - fechaDesde.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    if (diffDays > 31) {
      setDateRangeError(`El rango seleccionado es de ${diffDays} días. Solo se permiten hasta 31 días.`);
      setAllData([]);
      return;
    } else {
      setDateRangeError("");
    }
  }, [dateAfter, dateBefore]);

  // Cargar datos con debounce - DESHABILITADO
  // useEffect(() => {
  //   if (!dateAfter || !dateBefore || dateRangeError || !hasUserInteracted) return;
  //   const timeoutId = setTimeout(() => loadData(), 500);
  //   return () => clearTimeout(timeoutId);
  // }, [dateAfter, dateBefore, dateRangeError, hasUserInteracted]);

  // Obtener listas de ciudades y canales desde el endpoint
  const ciudadesDisponibles = useMemo(() => {
    if (!filtrosData?.data) return [];
    return (filtrosData.data as any).ciudades || [];
  }, [filtrosData]);

  const canalesDisponibles = useMemo(() => {
    if (!filtrosData?.data) return [];
    return (filtrosData.data as any).canales || [];
  }, [filtrosData]);

  // Tipos estáticos y planes estáticos
  const tiposDisponibles = ["Instalacion", "Migracion", "Traslado"];
  const planesDisponibles = ["Home", "Corporativo", "Pymes", "ISP"];

  // Filtrar ciudades por búsqueda
  const ciudadesFiltradas = useMemo(() => {
    if (!searchCiudad) return ciudadesDisponibles;
    return ciudadesDisponibles.filter((ciudad: string) =>
      ciudad.toLowerCase().includes(searchCiudad.toLowerCase())
    );
  }, [ciudadesDisponibles, searchCiudad]);

  // Filtrar canales por búsqueda
  const canalesFiltrados = useMemo(() => {
    if (!searchCanal) return canalesDisponibles;
    return canalesDisponibles.filter((canal: string) =>
      canal.toLowerCase().includes(searchCanal.toLowerCase())
    );
  }, [canalesDisponibles, searchCanal]);

  // Filtrar planes por búsqueda
  const planesFiltrados = useMemo(() => {
    if (!searchPlan) return planesDisponibles;
    return planesDisponibles.filter((plan: string) =>
      plan.toLowerCase().includes(searchPlan.toLowerCase())
    );
  }, [planesDisponibles, searchPlan]);

  // Filtrar tipos por búsqueda
  const tiposFiltrados = useMemo(() => {
    if (!searchTipo) return tiposDisponibles;
    return tiposDisponibles.filter((tipo: string) =>
      tipo.toLowerCase().includes(searchTipo.toLowerCase())
    );
  }, [tiposDisponibles, searchTipo]);

  // Filtrar datos (todos los filtros se aplican en conjunto)
  const filteredData = useMemo(() => {
    let data = allData;

    // Filtrar automáticamente por ventas exitosas y pendientes (configuración interna)
    data = data.filter(item => {
      const estadoItemRaw = String(item.estado_venta || "").trim().toLowerCase();
      return estadoItemRaw === "exitosa" || estadoItemRaw === "pendiente";
    });

    // Filtrar automáticamente por estado_instalacion === 'OK' o 'SOLUCIONADO'
    data = data.filter(item => {
      const estadoInstalacion = String(item.estado_instalacion || "").trim().toUpperCase();
      return estadoInstalacion === "OK" || estadoInstalacion === "SOLUCIONADO";
    });

    if (ciudadFilter.length > 0) {
      // Filtrar valores válidos
      const ciudadesValidas = ciudadFilter.filter(c => c && c.trim());
      if (ciudadesValidas.length > 0) {
        data = data.filter(item => {
          const ciudadItem = item.ciudad || "";
          const ciudadNormalizada = ciudadItem.charAt(0).toUpperCase() + ciudadItem.slice(1).toLowerCase();
          return ciudadesValidas.some(ciudad =>
            ciudad && ciudadNormalizada.toUpperCase().includes(ciudad.toUpperCase())
          );
        });
      }
    }

    if (canalFilter.length > 0) {
      // Filtrar valores válidos
      const canalesValidos = canalFilter.filter(c => c && c.trim());
      if (canalesValidos.length > 0) {
        data = data.filter(item =>
          canalesValidos.some(canal =>
            canal && String(item.cargo || "").toUpperCase().includes(canal.toUpperCase())
          )
        );
      }
    }

    if (planFilter.length > 0) {
      // Filtrar valores válidos
      const planesValidos = planFilter.filter(p => p && p.trim());
      if (planesValidos.length > 0) {
        data = data.filter(item =>
          planesValidos.some(plan =>
            plan && String(item.tipo_plan || "").toUpperCase().includes(plan.toUpperCase())
          )
        );
      }
    }

    if (tipoFilter.length > 0) {
      // Filtrar valores válidos
      const tiposValidos = tipoFilter.filter(t => t && t.trim());
      if (tiposValidos.length > 0) {
        data = data.filter(item =>
          tiposValidos.some(tipo =>
            tipo && String(item.tipo || "").toUpperCase().includes(tipo.toUpperCase())
          )
        );
      }
    }

    return data;
  }, [allData, ciudadFilter, canalFilter, planFilter, tipoFilter]);

  // Calcular resumen por ciudades (normalizar mayúsculas)
  const resumenCiudades = useMemo(() => {
    const ciudades: Record<string, { cantidad: number; monto: number }> = {};
    filteredData.forEach(item => {
      const ciudadRaw = item.ciudad || "Sin Ciudad";
      // Normalizar: Primera letra mayúscula, resto minúscula
      const ciudad = ciudadRaw.charAt(0).toUpperCase() + ciudadRaw.slice(1).toLowerCase();
      const valor = parseFloat(item.valor_mensual) || 0;
      if (!ciudades[ciudad]) {
        ciudades[ciudad] = { cantidad: 0, monto: 0 };
      }
      ciudades[ciudad].cantidad++;
      ciudades[ciudad].monto += valor;
    });
    return Object.entries(ciudades)
      .map(([ciudad, data]) => ({ ciudad, ...data }))
      .sort((a, b) => b.cantidad - a.cantidad);
  }, [filteredData]);

  // Calcular resumen por canales (usar campo "cargo")
  const resumenCanales = useMemo(() => {
    const canales: Record<string, { cantidad: number; monto: number }> = {};
    filteredData.forEach(item => {
      const canal = item.cargo || "Sin Canal";
      const valor = parseFloat(item.valor_mensual) || 0;
      if (!canales[canal]) {
        canales[canal] = { cantidad: 0, monto: 0 };
      }
      canales[canal].cantidad++;
      canales[canal].monto += valor;
    });
    return Object.entries(canales)
      .map(([canal, data]) => ({ canal, ...data }))
      .sort((a, b) => b.cantidad - a.cantidad);
  }, [filteredData]);

  // Totales generales
  const totales = useMemo(() => {
    const totalPlanes = filteredData.length;
    const totalMonto = filteredData.reduce((sum, item) => sum + (parseFloat(item.valor_mensual) || 0), 0);

    // Separar por estado de venta
    let totalExitosas = 0;
    let totalPendientes = 0;
    let montoExitosas = 0;
    let montoPendientes = 0;

    filteredData.forEach(item => {
      const estadoVenta = String(item.estado_venta || "").trim().toLowerCase();
      const valor = parseFloat(item.valor_mensual) || 0;

      if (estadoVenta === "exitosa") {
        totalExitosas++;
        montoExitosas += valor;
      } else if (estadoVenta === "pendiente") {
        totalPendientes++;
        montoPendientes += valor;
      }
    });

    return {
      totalPlanes,
      totalMonto,
      totalExitosas,
      totalPendientes,
      montoExitosas,
      montoPendientes
    };
  }, [filteredData]);

  // Calcular planes más vendidos
  const planesMasVendidos = useMemo(() => {
    const planes: Record<string, number> = {};
    filteredData.forEach(item => {
      const plan = item.nombre_plan_factura?.trim();
      if (plan) {
        planes[plan] = (planes[plan] || 0) + 1;
      }
    });
    return Object.entries(planes)
      .map(([plan, cantidad]) => ({ plan, cantidad }))
      .sort((a, b) => b.cantidad - a.cantidad);
  }, [filteredData]);

  // Calcular promedio de ventas
  const promedioVentas = useMemo(() => {
    if (filteredData.length === 0) return 0;
    return totales.totalMonto / filteredData.length;
  }, [filteredData, totales.totalMonto]);

  // Calcular ventas por tipo
  const ventasPorTipo = useMemo(() => {
    const tipos = {
      Instalacion: 0,
      Migracion: 0,
      Traslado: 0
    };
    filteredData.forEach(item => {
      const tipo = String(item.tipo || "").trim();
      const valor = parseFloat(item.valor_mensual) || 0;
      if (tipo.toLowerCase() === "instalacion") {
        tipos.Instalacion += valor;
      } else if (tipo.toLowerCase() === "migracion") {
        tipos.Migracion += valor;
      } else if (tipo.toLowerCase() === "traslado") {
        tipos.Traslado += valor;
      }
    });
    return tipos;
  }, [filteredData]);

  const handleCleanFilter = () => {
    setCiudadFilter([]);
    setCanalFilter([]);
    setPlanFilter([]);
    setTipoFilter([]);
    cleanFilter();
    const startDate = new Date(firstDayPrevMonth);
    const endDate = new Date(lastDayPrevMonth);
    onChangeRangeDate([startDate, endDate]);
  };

  const handleSearch = async () => {
    await loadData();
  };

  const handleRefreshFiltros = async () => {
    await refetchFiltros();
  };

  // Exportar Excel
  const handleExportExcel = async () => {
    setIsExporting(true);
    setExportType("excel");
    setExportProgress(0);

    try {
      setExportProgress(30);
      const wb = XLSX.utils.book_new();

      // Hoja de resumen
      const resumenData = [
        ["RESUMEN DE VENTAS"],
        [],
        ["FILTROS APLICADOS"],
        ["Fecha desde:", dateAfter || firstDayPrevMonth],
        ["Fecha hasta:", dateBefore || lastDayPrevMonth],
        ...(ciudadFilter.length > 0 ? [["Ciudades:", ciudadFilter.join(", ")]] : []),
        ...(canalFilter.length > 0 ? [["Canales:", canalFilter.join(", ")]] : []),
        ...(planFilter.length > 0 ? [["Planes:", planFilter.join(", ")]] : []),
        ...(tipoFilter.length > 0 ? [["Tipo:", tipoFilter.join(", ")]] : []),
        [],
        ["TOTALES GENERALES"],
        ["Total de planes:", totales.totalPlanes],
        ["Total en dólares:", `$${totales.totalMonto.toFixed(2)}`],
        ["ARPU:", `$${promedioVentas.toFixed(2)}`],
        [],
        ["VENTAS POR TIPO"],
        ["Instalación:", `$${ventasPorTipo.Instalacion.toFixed(2)}`],
        ["Migración:", `$${ventasPorTipo.Migracion.toFixed(2)}`],
        ["Traslado:", `$${ventasPorTipo.Traslado.toFixed(2)}`],
        [],
        ["PLANES MÁS VENDIDOS"],
        ["Posición", "Plan", "Cantidad", "Porcentaje"],
        ...planesMasVendidos.map((p, idx) => [
          idx + 1,
          p.plan,
          p.cantidad,
          `${((p.cantidad / totales.totalPlanes) * 100).toFixed(2)}%`
        ]),
        [],
        ["RESUMEN POR CIUDADES"],
        ["Ciudad", "Cantidad", "Monto ($)", "Promedio ($)"],
        ...resumenCiudades.map(c => [
          c.ciudad,
          c.cantidad,
          c.monto.toFixed(2),
          (c.monto / c.cantidad).toFixed(2)
        ]),
        [],
        ["RESUMEN POR CANALES"],
        ["Canal", "Cantidad", "Monto ($)", "Promedio ($)"],
        ...resumenCanales.map(c => [
          c.canal,
          c.cantidad,
          c.monto.toFixed(2),
          (c.monto / c.cantidad).toFixed(2)
        ]),
      ];

      setExportProgress(70);
      const ws = XLSX.utils.aoa_to_sheet(resumenData);
      XLSX.utils.book_append_sheet(wb, ws, "Resumen");
      XLSX.writeFile(wb, `Resumen_Ventas_${format(new Date(), "yyyy-MM-dd_HH-mm")}.xlsx`);

      setExportProgress(100);
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error("Error al exportar Excel:", error);
    } finally {
      setIsExporting(false);
      setExportType("");
      setExportProgress(0);
    }
  };

  // Exportar PDF
  const handleExportPDF = async () => {
    setIsExporting(true);
    setExportType("pdf");
    setExportProgress(0);

    try {
      setExportProgress(20);
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Cargar imagen del encabezado
      const headerImageUrl = "https://sigefstorage.tvmax.ec/erp-dev/image/empresa-logo/headerfibra.png";
      let y = 20;

      try {
        const img = await loadImage(headerImageUrl);
        const headerHeight = 18;
        doc.addImage(img, "PNG", 0, 0, pageWidth, headerHeight);
        y = headerHeight + 5;
      } catch {
        // Si falla la imagen, usar encabezado de color
        doc.setFillColor(25, 118, 210);
        doc.rect(0, 0, pageWidth, 18, "F");
        y = 23;
      }

      // Título
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text("RESUMEN DE VENTAS", pageWidth / 2, y, { align: "center" });
      y += 15;

      setExportProgress(40);

      // Filtros aplicados
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("FILTROS APLICADOS", 20, y);
      y += 7;
      doc.setFont("helvetica", "normal");
      doc.text(`Fechas: ${dateAfter || firstDayPrevMonth} al ${dateBefore || lastDayPrevMonth}`, 25, y);
      y += 6;
      if (ciudadFilter.length > 0) {
        doc.text(`Ciudades: ${ciudadFilter.join(", ")}`, 25, y);
        y += 6;
      }
      if (canalFilter.length > 0) {
        doc.text(`Canales: ${canalFilter.join(", ")}`, 25, y);
        y += 6;
      }
      if (planFilter.length > 0) {
        doc.text(`Planes: ${planFilter.join(", ")}`, 25, y);
        y += 6;
      }
      if (tipoFilter.length > 0) {
        doc.text(`Tipo: ${tipoFilter.join(", ")}`, 25, y);
        y += 6;
      }
      y += 10;

      // Totales
      doc.setFont("helvetica", "bold");
      doc.text("TOTALES GENERALES", 20, y);
      y += 7;
      doc.setFont("helvetica", "normal");
      doc.text(`Total de planes: ${totales.totalPlanes}`, 25, y);
      y += 6;
      doc.text(`Total en dólares: $${totales.totalMonto.toFixed(2)}`, 25, y);
      y += 6;
      doc.text(`ARPU: $${promedioVentas.toFixed(2)}`, 25, y);
      y += 10;

      // Ventas por tipo
      doc.setFont("helvetica", "bold");
      doc.text("VENTAS POR TIPO", 20, y);
      y += 7;
      doc.setFont("helvetica", "normal");
      doc.text(`Instalación: $${ventasPorTipo.Instalacion.toFixed(2)}`, 25, y);
      y += 6;
      doc.text(`Migración: $${ventasPorTipo.Migracion.toFixed(2)}`, 25, y);
      y += 6;
      doc.text(`Traslado: $${ventasPorTipo.Traslado.toFixed(2)}`, 25, y);
      y += 15;

      setExportProgress(50);

      // Planes Más Vendidos (top 10)
      doc.setFont("helvetica", "bold");
      doc.text("PLANES MÁS VENDIDOS (Top 10)", 20, y);
      y += 7;
      doc.setFont("helvetica", "normal");
      planesMasVendidos.slice(0, 10).forEach((p, idx) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        const porcentaje = ((p.cantidad / totales.totalPlanes) * 100).toFixed(2);
        doc.text(`${idx + 1}. ${p.plan}: ${p.cantidad} ventas (${porcentaje}%)`, 25, y);
        y += 6;
      });
      y += 10;

      setExportProgress(60);

      // Ciudades
      doc.setFont("helvetica", "bold");
      doc.text("RESUMEN POR CIUDADES", 20, y);
      y += 7;
      doc.setFont("helvetica", "normal");
      resumenCiudades.slice(0, 10).forEach(c => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        const promedio = (c.monto / c.cantidad).toFixed(2);
        doc.text(`${c.ciudad}: ${c.cantidad} planes - $${c.monto.toFixed(2)} (Prom: $${promedio})`, 25, y);
        y += 6;
      });
      y += 10;

      setExportProgress(80);

      // Canales
      if (y > 250) {
        doc.addPage();
        y = 20;
      }
      doc.setFont("helvetica", "bold");
      doc.text("RESUMEN POR CANALES", 20, y);
      y += 7;
      doc.setFont("helvetica", "normal");
      resumenCanales.forEach(c => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        const promedio = (c.monto / c.cantidad).toFixed(2);
        doc.text(`${c.canal}: ${c.cantidad} planes - $${c.monto.toFixed(2)} (Prom: $${promedio})`, 25, y);
        y += 6;
      });

      // Agregar pie de página en todas las páginas
      const totalPages = (doc as any).getNumberOfPages ? (doc as any).getNumberOfPages() : (doc as any).internal.getNumberOfPages();
      const fechaHoraActual = format(new Date(), "dd/MM/yyyy HH:mm:ss");

      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);

        // Línea superior del pie de página
        doc.text(
          `Informe obtenido del sistema EVA - Fibramax el ${fechaHoraActual}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" }
        );

        // Línea inferior del pie de página
        doc.setFont("helvetica", "bold");
        doc.text(
          "Documento Confidencial",
          pageWidth / 2,
          pageHeight - 6,
          { align: "center" }
        );
        doc.setFont("helvetica", "normal");

        // Número de página en la esquina derecha
        doc.text(
          `Página ${i} de ${totalPages}`,
          pageWidth - 15,
          pageHeight - 6,
          { align: "right" }
        );
      }

      doc.save(`Resumen_Ventas_${format(new Date(), "yyyy-MM-dd_HH-mm")}.pdf`);

      setExportProgress(100);
      await new Promise(resolve => setTimeout(resolve, 800));
    } catch (error) {
      console.error("Error al exportar PDF:", error);
    } finally {
      setIsExporting(false);
      setExportType("");
      setExportProgress(0);
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Mensaje informativo */}
      <Grid item xs={12}>
        <Alert severity="info" sx={{ bgcolor: "info.light" }}>
          <Typography variant="body2">
            <strong>Información:</strong> Se mostrarán datos de los últimos 31 días.
            Se permite obtener un rango máximo de 31 días por consulta.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>Nota:</strong> Solo se muestran ventas con estado de instalación "OK" o "Solucionado".
          </Typography>
        </Alert>
      </Grid>

      {/* Error de rango de fechas */}
      {dateRangeError && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setDateRangeError("")}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {dateRangeError}
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Por favor, ajuste el rango de fechas a máximo 31 días.
            </Typography>
          </Alert>
        </Grid>
      )}

      {/* Error de carga de datos */}
      {error && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setError("")}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Error al cargar datos
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {error}
            </Typography>
          </Alert>
        </Grid>
      )}

      {/* Filtros - Orden: Fecha, Segmentación Plan, Canal, Tipo, Ciudad */}
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <DateRangePicker
          customStyle={{
            height: '40px',
            borderRadius: '4px',
          }}
          size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
          flatpickrRef={flatpickrRef}
          onChange={(range) => onChangeRangeDate(range)}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={2}>
        <FormControl fullWidth size="small">
          <InputLabel>Segmentación de planes</InputLabel>
          <Select
            multiple
            value={planFilter}
            onChange={(e) => {
              const value = e.target.value as string[];
              if (value.includes("SELECCIONAR_TODOS")) {
                setPlanFilter(planFilter.length === planesDisponibles.length ? [] : planesDisponibles);
              } else {
                setPlanFilter(value);
              }
            }}
            label="Segmentación de planes"
            renderValue={(selected) => selected.length === planesDisponibles.length ? "Todos" : selected.join(", ")}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            <Box sx={{ px: 1.5, py: 0.75, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
              <TextField
                size="small"
                placeholder="Buscar plan..."
                fullWidth
                value={searchPlan}
                onChange={(e) => setSearchPlan(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              />
            </Box>
            <MenuItem value="SELECCIONAR_TODOS" dense disabled={searchPlan.trim() !== ""}>
              <Checkbox checked={planFilter.length === planesDisponibles.length} size="small" disabled={searchPlan.trim() !== ""} />
              <ListItemText primary="Seleccionar todos" primaryTypographyProps={{ fontSize: '0.875rem' }} />
            </MenuItem>
            {planesFiltrados.length > 0 ? (
              planesFiltrados.map((plan) => (
                <MenuItem key={plan} value={plan} dense>
                  <Checkbox checked={planFilter.indexOf(plan) > -1} size="small" />
                  <ListItemText primary={plan} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled dense>
                <ListItemText primary="No se encontraron planes" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={2}>
        <FormControl fullWidth size="small">
          <InputLabel>Canal</InputLabel>
          <Select
            multiple
            value={canalFilter}
            onChange={(e) => {
              const value = e.target.value as string[];
              if (value.includes("SELECCIONAR_TODOS")) {
                setCanalFilter(canalFilter.length === canalesDisponibles.length ? [] : canalesDisponibles);
              } else {
                setCanalFilter(value);
              }
            }}
            label="Canal"
            disabled={isLoadingFiltros || canalesDisponibles.length === 0}
            renderValue={(selected) => selected.length === canalesDisponibles.length ? "Todos" : selected.join(", ")}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            <Box sx={{ px: 1.5, py: 0.75, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
              <TextField
                size="small"
                placeholder="Buscar canal..."
                fullWidth
                value={searchCanal}
                onChange={(e) => setSearchCanal(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              />
            </Box>
            <MenuItem value="SELECCIONAR_TODOS" dense disabled={searchCanal.trim() !== ""}>
              <Checkbox checked={canalFilter.length === canalesDisponibles.length} size="small" disabled={searchCanal.trim() !== ""} />
              <ListItemText primary="Seleccionar todos" primaryTypographyProps={{ fontSize: '0.875rem' }} />
            </MenuItem>
            {canalesFiltrados.length > 0 ? (
              canalesFiltrados.map((canal: string) => (
                <MenuItem key={canal} value={canal} dense>
                  <Checkbox checked={canalFilter.indexOf(canal) > -1} size="small" />
                  <ListItemText primary={canal} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled dense>
                <ListItemText primary="No se encontraron canales" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={2}>
        <FormControl fullWidth size="small">
          <InputLabel>Tipo</InputLabel>
          <Select
            multiple
            value={tipoFilter}
            onChange={(e) => {
              const value = e.target.value as string[];
              if (value.includes("SELECCIONAR_TODOS")) {
                setTipoFilter(tipoFilter.length === tiposDisponibles.length ? [] : tiposDisponibles);
              } else {
                setTipoFilter(value);
              }
            }}
            label="Tipo"
            renderValue={(selected) => selected.length === tiposDisponibles.length ? "Todos" : selected.join(", ")}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            <Box sx={{ px: 1.5, py: 0.75, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
              <TextField
                size="small"
                placeholder="Buscar tipo..."
                fullWidth
                value={searchTipo}
                onChange={(e) => setSearchTipo(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              />
            </Box>
            <MenuItem value="SELECCIONAR_TODOS" dense disabled={searchTipo.trim() !== ""}>
              <Checkbox checked={tipoFilter.length === tiposDisponibles.length} size="small" disabled={searchTipo.trim() !== ""} />
              <ListItemText primary="Seleccionar todos" primaryTypographyProps={{ fontSize: '0.875rem' }} />
            </MenuItem>
            {tiposFiltrados.length > 0 ? (
              tiposFiltrados.map((tipo) => (
                <MenuItem key={tipo} value={tipo} dense>
                  <Checkbox checked={tipoFilter.indexOf(tipo) > -1} size="small" />
                  <ListItemText primary={tipo} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled dense>
                <ListItemText primary="No se encontraron tipos" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6} md={3} lg={2}>
        <FormControl fullWidth size="small">
          <InputLabel>Ciudad</InputLabel>
          <Select
            multiple
            value={ciudadFilter}
            onChange={(e) => {
              const value = e.target.value as string[];
              if (value.includes("SELECCIONAR_TODOS")) {
                setCiudadFilter(ciudadFilter.length === ciudadesDisponibles.length ? [] : ciudadesDisponibles);
              } else {
                setCiudadFilter(value);
              }
            }}
            label="Ciudad"
            disabled={isLoadingFiltros || ciudadesDisponibles.length === 0}
            renderValue={(selected) => selected.length === ciudadesDisponibles.length ? "Todos" : selected.join(", ")}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            <Box sx={{ px: 1.5, py: 0.75, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
              <TextField
                size="small"
                placeholder="Buscar ciudad..."
                fullWidth
                value={searchCiudad}
                onChange={(e) => setSearchCiudad(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              />
            </Box>
            <MenuItem value="SELECCIONAR_TODOS" dense disabled={searchCiudad.trim() !== ""}>
              <Checkbox checked={ciudadFilter.length === ciudadesDisponibles.length} size="small" disabled={searchCiudad.trim() !== ""} />
              <ListItemText primary="Seleccionar todos" primaryTypographyProps={{ fontSize: '0.875rem' }} />
            </MenuItem>
            {ciudadesFiltradas.length > 0 ? (
              ciudadesFiltradas.map((ciudad: string) => (
                <MenuItem key={ciudad} value={ciudad} dense>
                  <Checkbox checked={ciudadFilter.indexOf(ciudad) > -1} size="small" />
                  <ListItemText primary={ciudad} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled dense>
                <ListItemText primary="No se encontraron ciudades" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6} md="auto" lg="auto" sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title="Recargar Canales y Ciudades">
          <IconButton
            color="primary"
            onClick={handleRefreshFiltros}
            disabled={isLoadingFiltros}
            size="small"
            sx={{
              border: 1,
              borderColor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.light',
                borderColor: 'primary.dark'
              },
              '&.Mui-disabled': {
                borderColor: 'action.disabled'
              }
            }}
          >
            {isLoadingFiltros ? <CircularProgress size={20} /> : <Refresh />}
          </IconButton>
        </Tooltip>
      </Grid>

      {/* Botones */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            startIcon={<Search />}
            onClick={handleSearch}
            disabled={dateRangeError !== ""}
            sx={{
              flex: { xs: 1, sm: '0 1 auto' },
              minWidth: { xs: 'auto', sm: 140 },
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': { boxShadow: 4 }
            }}
          >
            Consultar
          </Button>

          <Button
            variant="outlined"
            color="primary"
            size="medium"
            startIcon={<FilterAltOffIcon />}
            onClick={handleCleanFilter}
            sx={{
              flex: { xs: 1, sm: '0 1 auto' },
              minWidth: { xs: 'auto', sm: 140 },
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Limpiar Filtros
          </Button>

          <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />

          <Button
            variant="contained"
            color="success"
            size="medium"
            startIcon={<Description />}
            onClick={handleExportExcel}
            disabled={filteredData.length === 0}
            sx={{
              flex: { xs: 1, sm: '0 1 auto' },
              minWidth: { xs: 'auto', sm: 140 },
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': { boxShadow: 4 }
            }}
          >
            Excel
          </Button>

          <Button
            variant="contained"
            color="error"
            size="medium"
            startIcon={<PictureAsPdf />}
            onClick={handleExportPDF}
            disabled={filteredData.length === 0}
            sx={{
              flex: { xs: 1, sm: '0 1 auto' },
              minWidth: { xs: 'auto', sm: 140 },
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': { boxShadow: 4 }
            }}
          >
            PDF
          </Button>
        </Box>
      </Grid>

      {/* Loading */}
      {isLoading && (
        <Grid item xs={12}>
          <Box sx={{ width: "100%" }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Cargando datos...
            </Typography>
            <LinearProgress />
          </Box>
        </Grid>
      )}

      {/* Filtros Aplicados */}
      {!isLoading && filteredData.length > 0 && (
        <Grid item xs={12}>
          <Card sx={{ bgcolor: "grey.50", border: 1, borderColor: "grey.300" }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                Filtros Aplicados:
              </Typography>
              <Stack direction="column" spacing={1}>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Fechas:</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {dateAfter || firstDayPrevMonth} al {dateBefore || lastDayPrevMonth}
                    </Typography>
                  </Box>
                  {planFilter.length > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Planes:</Typography>
                      <Typography variant="body2" color="text.secondary">{planFilter.join(", ")}</Typography>
                    </Box>
                  )}
                  {ciudadFilter.length > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Ciudades:</Typography>
                      <Typography variant="body2" color="text.secondary">{ciudadFilter.join(", ")}</Typography>
                    </Box>
                  )}
                  {canalFilter.length > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Canales:</Typography>
                      <Typography variant="body2" color="text.secondary">{canalFilter.join(", ")}</Typography>
                    </Box>
                  )}
                  {tipoFilter.length > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Tipo:</Typography>
                      <Typography variant="body2" color="text.secondary">{tipoFilter.join(", ")}</Typography>
                    </Box>
                  )}
                  {ciudadFilter.length === 0 && canalFilter.length === 0 && planFilter.length === 0 && tipoFilter.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                      (Sin filtros adicionales aplicados)
                    </Typography>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic", mt: 0.5 }}>
                  * Los datos mostrados corresponden únicamente a ventas exitosas y pendientes con instalaciones OK o Solucionado.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Totales Generales */}
      {!isLoading && filteredData.length > 0 && (
        <>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Totales Generales
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={4}>
                <Card sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  height: { xs: 160, md: 176 },
                  borderRadius: 3,
                  boxShadow: 1
                }}>
                  <CardContent sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    p: 2.5,
                    "&:last-child": { pb: 2.5 }
                  }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "rgba(255,255,255,0.9)" }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>📊 Total de Planes Vendidos</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
                        {totales.totalPlanes}
                      </Typography>
                      <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.25 }}>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.75rem" }}>
                          Total Exitosas: {totales.totalExitosas}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.75rem" }}>
                          Total Pendientes: {totales.totalPendientes}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => setOpenModalPlanes(true)}
                      sx={{
                        alignSelf: "flex-start",
                        color: "white",
                        borderColor: "rgba(255,255,255,0.6)",
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.5,
                        fontSize: "0.875rem",
                        "&:hover": {
                          borderColor: "rgba(255,255,255,0.8)",
                          bgcolor: "rgba(255,255,255,0.1)"
                        }
                      }}
                    >
                      Ver Detalles
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{
                  bgcolor: "success.main",
                  color: "white",
                  height: { xs: 160, md: 176 },
                  borderRadius: 3,
                  boxShadow: 1
                }}>
                  <CardContent sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    p: 2.5,
                    "&:last-child": { pb: 2.5 }
                  }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "rgba(255,255,255,0.9)" }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>💰 Total en Ventas</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
                        ${totales.totalMonto.toFixed(2)}
                      </Typography>
                      <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.25 }}>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.75rem" }}>
                          Valor Exitosas: ${totales.montoExitosas.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.75rem" }}>
                          Valor Pendientes: ${totales.montoPendientes.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => setOpenModalVentas(true)}
                      sx={{
                        alignSelf: "flex-start",
                        color: "white",
                        borderColor: "rgba(255,255,255,0.6)",
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.5,
                        fontSize: "0.875rem",
                        "&:hover": {
                          borderColor: "rgba(255,255,255,0.8)",
                          bgcolor: "rgba(255,255,255,0.1)"
                        }
                      }}
                    >
                      Ver Detalles
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{
                  bgcolor: "info.main",
                  color: "white",
                  height: { xs: 160, md: 176 },
                  borderRadius: 3,
                  boxShadow: 1
                }}>
                  <CardContent sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    p: 2.5,
                    "&:last-child": { pb: 2.5 }
                  }}>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)", mb: 1, fontWeight: 700 }}>
                      📈 Ventas por Tipo
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, justifyContent: "space-evenly" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body2">Instalación:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          ${ventasPorTipo.Instalacion.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body2">Migración:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          ${ventasPorTipo.Migracion.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body2">Traslado:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          ${ventasPorTipo.Traslado.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Resumen por Ciudades */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocationCity color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Resumen por Ciudades
                    </Typography>
                  </Box>
                  <Tooltip title={vistaTabla ? "Vista de barras" : "Vista de tabla"}>
                    <IconButton size="small" onClick={() => setVistaTabla(!vistaTabla)}>
                      {vistaTabla ? <BarChart /> : <ViewList />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {!vistaTabla ? (
                  // Vista de barras
                  <>
                    {resumenCiudades.map((ciudad, idx) => (
                      <Box key={idx} sx={{ mb: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {ciudad.ciudad}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {ciudad.cantidad} planes - ${ciudad.monto.toFixed(2)}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(ciudad.cantidad / totales.totalPlanes) * 100}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    ))}
                  </>
                ) : (
                  // Vista de tabla
                  <TableContainer component={Paper} sx={{ boxShadow: 0 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Ciudad</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Cantidad</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Monto ($)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {resumenCiudades.map((ciudad, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{ciudad.ciudad}</TableCell>
                            <TableCell align="right">{ciudad.cantidad}</TableCell>
                            <TableCell align="right">${ciudad.monto.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Resumen por Canales */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Category color="secondary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Resumen por Canales
                    </Typography>
                  </Box>
                  <Tooltip title={vistaTabla ? "Vista de barras" : "Vista de tabla"}>
                    <IconButton size="small" onClick={() => setVistaTabla(!vistaTabla)}>
                      {vistaTabla ? <BarChart /> : <ViewList />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {!vistaTabla ? (
                  // Vista de barras
                  <>
                    {resumenCanales.map((canal, idx) => (
                      <Box key={idx} sx={{ mb: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {canal.canal}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {canal.cantidad} planes - ${canal.monto.toFixed(2)}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(canal.cantidad / totales.totalPlanes) * 100}
                          sx={{ height: 6, borderRadius: 3 }}
                          color="secondary"
                        />
                      </Box>
                    ))}
                  </>
                ) : (
                  // Vista de tabla
                  <TableContainer component={Paper} sx={{ boxShadow: 0 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Canal</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Cantidad</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Monto ($)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {resumenCanales.map((canal, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{canal.canal}</TableCell>
                            <TableCell align="right">{canal.cantidad}</TableCell>
                            <TableCell align="right">${canal.monto.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </>
      )}

      {/* Modal de Planes Más Vendidos */}
      <Dialog
        open={openModalPlanes}
        onClose={() => setOpenModalPlanes(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Planes Más Vendidos
            </Typography>
            <IconButton size="small" onClick={() => setOpenModalPlanes(false)}>
              <FilterAltOffIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Posición</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Cantidad Vendida</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Porcentaje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {planesMasVendidos.map((plan, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{plan.plan}</TableCell>
                    <TableCell align="right">{plan.cantidad}</TableCell>
                    <TableCell align="right">
                      {((plan.cantidad / totales.totalPlanes) * 100).toFixed(2)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>

      {/* Modal de Promedio de Ventas */}
      <Dialog
        open={openModalVentas}
        onClose={() => setOpenModalVentas(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Análisis de Ventas
            </Typography>
            <IconButton size="small" onClick={() => setOpenModalVentas(false)}>
              <FilterAltOffIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3, mt: 2 }}>
            <Card sx={{ bgcolor: "success.light", color: "white" }}>
              <CardContent>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  ARPU
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  ${promedioVentas.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Promedio por Ciudad
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Ciudad</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Cantidad Ventas</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total ($)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Promedio ($)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resumenCiudades.map((ciudad, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{ciudad.ciudad}</TableCell>
                    <TableCell align="right">{ciudad.cantidad}</TableCell>
                    <TableCell align="right">${ciudad.monto.toFixed(2)}</TableCell>
                    <TableCell align="right">
                      ${(ciudad.monto / ciudad.cantidad).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Promedio por Canal
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Canal</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Cantidad Ventas</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total ($)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Promedio ($)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resumenCanales.map((canal, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{canal.canal}</TableCell>
                    <TableCell align="right">{canal.cantidad}</TableCell>
                    <TableCell align="right">${canal.monto.toFixed(2)}</TableCell>
                    <TableCell align="right">
                      ${(canal.monto / canal.cantidad).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>

      {/* Modal de exportación */}
      <Dialog
        open={isExporting}
        disableEscapeKeyDown
        PaperProps={{ sx: { minWidth: 400, borderRadius: 2 } }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            {exportType === "excel" ? (
              <Description sx={{ fontSize: 48, color: 'success.main' }} />
            ) : (
              <PictureAsPdf sx={{ fontSize: 48, color: 'error.main' }} />
            )}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Exportando {exportType === "excel" ? "Excel" : "PDF"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                {exportProgress < 50 ? "Preparando datos..." :
                  exportProgress < 80 ? "Generando archivo..." :
                    "Finalizando descarga..."}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={exportProgress}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              {exportProgress}% completado
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Grid>
  );
}
